import { Fragment, lazy, Suspense, useEffect, useMemo, useState } from "react";
import "react-datepicker/dist/react-datepicker.css";
import "react-phone-input-2/lib/style.css";
import "react-quill/dist/quill.snow.css";
import { Provider, useDispatch, useSelector } from "react-redux";
import {
  Navigate,
  Route,
  BrowserRouter as Router,
  Routes,
  useLocation,
} from "react-router-dom";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { PersistGate } from "redux-persist/integration/react";
import "./App.css";
import configureStoreProd from "./config/configureStore.prod";
import "./scss/main.scss";
import "react-datepicker/dist/react-datepicker.css";
import ApiClient from "./methods/api/apiClient";
import { isDebugMockUser, isGuestMode } from "./methods/guestMode";
import { active_plan_success, clear_plan_success } from "./actions/activePlan";
import PageLayout from "./components/global/PageLayout";
import ErrorBoundary from "./components/common/ErrorBoundary";
import ExplainerManager from "./components/ExplainerTour/ExplainerManager";
import DormantInviteClaimer from "./components/global/DormantInviteClaimer";
import ScrollRestoration from "./components/global/ScrollRestoration";
import CookieConsent from "./components/global/CookieConsent";
import { trackPageView } from "./analytics";
import { applySeo } from "./utils/seo";
import { CoachWebSocketProvider } from "./providers/CoachWebSocketProvider"; // Coach IA Phase 2
import { WhiteLabelProvider, setWhiteLabelSlug, useWhiteLabel } from "./providers/WhiteLabel/WhiteLabelProvider";
import { PAGE_IMPORTS } from "./pagesLazy";
import { isApp } from "./native/platform";

// Composants globaux chargés à la demande (hors bundle initial) :
// - SocialProofNotification tire framer-motion (~112 Ko)
// - AgentImmoAssistant = widget chat global
const SocialProofNotification = lazy(() => import("./components/global/SocialProof/SocialProofNotification"));
const AgentImmoAssistant = lazy(() => import("./components/Coach/AgentImmoAssistant"));

// Map des imports explicites de pages (cf. scripts/gen-pages-lazy.js), typée en
// record pour permettre l'accès par clé (route `path`).
const pageImports = PAGE_IMPORTS as Record<string, () => Promise<any>>;

const { persistor, store } = configureStoreProd();

// Exportés pour index.tsx : on attend la rehydratation du store AVANT le premier
// render afin de ne pas afficher l'état transitoire (« loading ... »).
export { persistor, store };

const PAGE_TITLES: Record<string, string> = {
  "/": "Accueil - AnyHomes",
  "/home-acheteurs": "Accueil acheteurs - AnyHomes",
  "/home-vendeurs": "Vendre mon bien - AnyHomes",
  "/dashboard": "Tableau de bord - AnyHomes",
  "/onboarding": "Onboarding - AnyHomes",
  "/properties": "Rechercher un bien - AnyHomes",
  "/property-details": "Détail du bien - AnyHomes",
  "/annonce": "Détail du bien - AnyHomes",
  "/followed-properties": "Wish liste - AnyHomes",
  "/followed-properties-list": "Biens suivis - AnyHomes",
  "/my-properties": "Mes biens - AnyHomes",
  "/property1": "Publier un bien - AnyHomes",
  "/property3": "Publier un bien - AnyHomes",
  "/property/add": "Ajouter un bien - AnyHomes",
  "/chat": "Messagerie - AnyHomes",
  "/notifications": "Notifications - AnyHomes",
  "/profile": "Mon profil - AnyHomes",
  "/buyer-file": "Dossier acheteur - AnyHomes",
  "/renter-file": "Dossier locataire - AnyHomes",
  "/seller-file": "Dossier vendeur - AnyHomes",
  "/transaction-dashboard": "Piloter ma transaction - AnyHomes",
  "/real-estate-transaction-owner": "Outil transactionnel - AnyHomes",
  "/real-estate-transaction-searcher": "Outil transactionnel - AnyHomes",
  "/dossier-visite": "Dossier de visite - AnyHomes",
  "/coach-immo": "Coach immo IA - AnyHomes",
  "/assistants-immo-ia": "Assistants immobiliers IA - AnyHomes",
  "/assistants-immo-ia-inapp": "Assistants immobiliers IA - AnyHomes",
  "/vos-reperes-immobiliers": "Vos repères immobiliers - AnyHomes",
  "/learning-center": "Learning center - AnyHomes",
  "/marketplace": "Services à la carte - AnyHomes",
  "/recherches": "Annonces de recherche - AnyHomes",
  "/services-a-la-carte": "Services immobiliers à la carte - AnyHomes",
  "/boite-a-outils": "Mes repères immo - AnyHomes",
  "/boite-a-outils/estimateur": "Estimateur de bien - AnyHomes",
  "/boite-a-outils/estimateur/demarrer": "Estimation de mon bien - AnyHomes",
  "/boite-a-outils/taxe-fonciere": "Taxe foncière - AnyHomes",
  "/boite-a-outils/carte-scolaire": "Carte scolaire - AnyHomes",
  "/boite-a-outils/prix-marche": "Prix du marché - AnyHomes",
  "/boite-a-outils/frais-notaire": "Frais de notaire - AnyHomes",
  "/boite-a-outils/taxe-habitation": "Taxe d'habitation - AnyHomes",
  "/boite-a-outils/applications": "Les applications - AnyHomes",
  "/boite-a-outils/mes-simulations": "Mes simulations - AnyHomes",
  "/boite-a-outils/mes-simulations-investissement": "Mes simulations de rentabilité - AnyHomes",
  "/boite-a-outils/rentabilite": "Simulateur de rentabilité locative - AnyHomes",
  "/boite-a-outils/rentabilite/demarrer": "Nouvelle simulation de rentabilité - AnyHomes",
  "/boite-a-outils/estimer-un-loyer": "Estimer un loyer - AnyHomes",
  "/boite-a-outils/estimer-un-loyer/demarrer": "Estimation de loyer - AnyHomes",
  "/boite-a-outils/mes-estimations-loyer": "Mes estimations de loyer - AnyHomes",
  "/boite-a-outils/fiscalite-revente": "Fiscalité de revente - AnyHomes",
  "/boite-a-outils/fiscalite-revente/demarrer": "Nouvelle simulation de fiscalité - AnyHomes",
  "/boite-a-outils/mes-simulations-fiscalite": "Mes simulations de fiscalité - AnyHomes",
  "/boite-a-outils/comprendre-une-ville": "Comprendre une ville - AnyHomes",
  "/boite-a-outils/connaitre-le-dpe": "Connaître le DPE - AnyHomes",
  "/boite-a-outils/analyser-un-dpe": "Analyse IA du DPE - AnyHomes",
  "/boite-a-outils/travaux": "Estimer mes travaux - AnyHomes",
  "/boite-a-outils/home-staging": "Home staging virtuel - AnyHomes",
  "/boite-a-outils/financabilite": "Mon projet d'achat est-il financable ? - AnyHomes",
  "/boite-a-outils/budget-occupation": "Budget mensuel d'occupation - AnyHomes",
  "/boite-a-outils/demenagement": "Estimer mon déménagement - AnyHomes",
  "/boite-a-outils/prix-maximal-offre": "Prix maximal de mon offre - AnyHomes",
  "/boite-a-outils/acheter-avant-de-vendre": "Acheter avant de vendre - AnyHomes",
  "/boite-a-outils/acheter-ou-rester-locataire": "Acheter ou rester locataire - AnyHomes",
  "/boite-a-outils/credit": "Simuler un crédit - AnyHomes",
  "/marketplace/favorites": "Services favoris - AnyHomes",
  "/marketplace/orders": "Mes commandes - AnyHomes",
  "/pro/payouts": "Versements - AnyHomes",
  "/agency/marketplace-sold-services": "MarketPlace dédiée - AnyHomes",
  "/past-transation-list": "Transactions récentes - AnyHomes",
  "/building-permit-list": "Permis de construire - AnyHomes",
  "/prolist": "Professionnels - AnyHomes",
  "/estimation": "Baromètre prix - AnyHomes",
  "/social-estimation": "Campagne baromètre - AnyHomes",
  "/serach-alert": "Alertes - AnyHomes",
  "/plan": "Abonnement - AnyHomes",
  "/billing-history": "Historique factures - AnyHomes",
  "/blogs": "Blog - AnyHomes",
  "/blog-detail": "Article - AnyHomes",
  "/contact-us": "Contact - AnyHomes",
  "/help": "Aide - AnyHomes",
  "/support-page": "Support - AnyHomes",
  "/signup": "Inscription - AnyHomes",
  "/activation-pro": "Activation compte pro - AnyHomes",
  "/login": "Connexion - AnyHomes",
  "/invite": "Invitation - AnyHomes",
  "/privacy-policy": "Politique de confidentialité - AnyHomes",
  "/cgu-cgv": "Conditions Générales d'Utilisation et de Vente - AnyHomes",
  "/proprietaire": "Propriétaire - AnyHomes",
  "/pro-offers": "Offre Pro - AnyHomes",
  "/pro-offers/agents": "Offre Agent indépendant - AnyHomes",
  "/pro-offers/hunters": "Offre Chasseur immobilier - AnyHomes",
  "/programme-pilote": "Programme pilote 50 agences - AnyHomes",
  "/revons-plus-grand": "Rêvons plus grand - AnyHomes",
  "/fonctionnalites": "Fonctionnalités - AnyHomes",
  "/acheter-avec-anyhomes": "Acheter - AnyHomes",
  "/vendre-avec-anyhomes": "Vendre - AnyHomes",
  "/offmarket": "Off market - AnyHomes",
  "/directory": "Annuaire - AnyHomes",
  "/transaction-tool": "Outil transactionnel - AnyHomes",
  "/transactions-historiques": "Transactions - AnyHomes",
  "/agent-immo-demande": "Agent immobilier - AnyHomes",
  "/estimation-entre-particuliers": "Estimation - AnyHomes",
  "/parcours-immo-digital": "Outil transactionnel - AnyHomes",
  "/outil-transactionnel": "Outil transactionnel - AnyHomes",
  "/vente-off-market": "Vente off market - AnyHomes",
  "/profil-bien": "Profil bien - AnyHomes",
  "/parrainage": "Parrainage - AnyHomes",
  "/parrainage-cgu": "CGU Parrainage - AnyHomes",
};

/**
 * SEO par page (publique / marketing) : description + type Open Graph.
 * Le <title> vient de PAGE_TITLES (appliqué par RouteLayoutShell).
 */
const PAGE_SEO: Record<string, { description?: string; type?: string }> = {
  "/": {
    description:
      "Trouvez votre prochain bien, consultez son profil et préparez votre projet d'achat ou de location avec AnyHomes.",
    type: "website",
  },
  "/home-acheteurs": {
    description: "Trouvez votre prochain bien, consultez son profil et préparez votre projet d'achat ou de location avec AnyHomes.",
    type: "website",
  },
  "/home-vendeurs": {
    description: "Vendez votre bien en direct avec AnyHomes : profil de bien pérenne, dossier vendeur, coach IA et professionnels à la carte.",
    type: "website",
  },
  "/acheter-avec-anyhomes": {
    description: "Acheter un bien immobilier plus sereinement : AnyHomes vous accompagne de la recherche à la signature.",
  },
  "/vendre-avec-anyhomes": {
    description: "Vendre votre bien immobilier plus vite : estimation, diffusion et suivi de vos acquéreurs avec AnyHomes.",
  },
  "/proprietaire": {
    description: "Propriétaire ? AnyHomes simplifie la gestion, l'estimation et la vente de vos biens immobiliers.",
  },
  "/pro-offers": {
    description: "Les offres professionnelles AnyHomes : outils et services pour les agents et agences immobilières.",
  },
  "/pro-offers/agents": {
    description: "L'offre AnyHomes pour les agents immobiliers indépendants : outils, services à la carte et mandats.",
  },
  "/pro-offers/hunters": {
    description: "L'offre AnyHomes pour les chasseurs immobiliers : Annuaire, assistants IA et analyse des biens.",
  },
  "/programme-pilote": {
    description: "Rejoignez les 50 agences du programme pilote AnyHomes : accompagnez les projets, préparez vos futurs mandats.",
  },
  "/revons-plus-grand": {
    description: "Rêvons plus grand avec AnyHomes : une nouvelle approche pour concrétiser vos projets immobiliers.",
  },
  "/estimation": {
    description: "Estimez votre bien immobilier gratuitement avec le baromètre prix AnyHomes, entre particuliers.",
  },
  "/blogs": {
    description: "Conseils et guides immobiliers : achat, vente, location et investissement avec AnyHomes.",
  },
  "/contact-us": {
    description: "Contactez l'équipe AnyHomes : une question, un projet immobilier ? Nous vous répondons.",
  },
  "/cgu-cgv": {
    description: "Conditions Générales d'Utilisation et de Vente de la plateforme AnyHomes.",
  },
  "/privacy-policy": {
    description: "Politique de confidentialité et protection des données personnelles de la plateforme AnyHomes.",
  },
  "/parrainage": {
    description: "Parrainez vos proches avec AnyHomes et profitez de vos avantages de parrainage.",
  },
  "/prolist": {
    description:
      "Trouvez un professionnel de l'immobilier : agences, agents, chasseurs, investisseurs et diagnostiqueurs immobiliers. Coordonnées, spécialités et services sur AnyHomes.",
  },
  "/marketplace": {
    description:
      "Services immobiliers à la carte : estimation, home staging, diagnostics, photographie, visites… Trouvez le bon professionnel et réservez en ligne sur AnyHomes.",
  },
  "/recherches": {
    description:
      "Découvrez les annonces de recherche immobilière publiées sur AnyHomes : vente, location, critères et zones recherchées par les particuliers et professionnels.",
  },
  "/learning-center": {
    description:
      "Le Learning Center AnyHomes : guides, conseils et vidéos pour acheter, vendre, louer et investir sereinement.",
  },
  "/boite-a-outils": {
    description:
      "Mes repères immo : estimation, prix du marché, taxe foncière, DPE, rentabilité… tous les outils pour préparer votre projet immobilier.",
  },
  "/boite-a-outils/applications": {
    description:
      "Découvrez les applications AnyHomes : estimateur de bien, prix du marché, carte scolaire, taxe foncière, DPE, rentabilité, loyer, travaux, crédit, fiscalité de revente…",
  },
  "/boite-a-outils/estimateur": {
    description:
      "Estimez la valeur de votre bien gratuitement : fourchette de prix, prix au m² et ventes comparables autour de chez vous.",
  },
  "/boite-a-outils/prix-marche": {
    description:
      "Analysez les prix du marché immobilier par ville et par quartier : prix au m², évolution et tendances.",
  },
  "/boite-a-outils/carte-scolaire": {
    description:
      "Consultez la carte scolaire et les établissements scolaires autour d'une adresse pour préparer votre projet familial.",
  },
  "/boite-a-outils/taxe-fonciere": {
    description:
      "Estimez votre taxe foncière selon votre commune et les caractéristiques de votre bien.",
  },
  "/boite-a-outils/taxe-habitation": {
    description:
      "Simulez votre taxe d'habitation selon votre situation et votre logement.",
  },
  "/boite-a-outils/frais-notaire": {
    description:
      "Calculez les frais de notaire et les droits de mutation pour votre achat immobilier.",
  },
  "/boite-a-outils/connaitre-le-dpe": {
    description:
      "Découvrez le DPE d'un bien et son impact sur sa valeur et ses performances énergétiques.",
  },
  "/boite-a-outils/analyser-un-dpe": {
    description:
      "Analysez un DPE en détail avec l'IA et identifiez les points forts, les risques et les travaux à prévoir.",
  },
  "/boite-a-outils/rentabilite": {
    description:
      "Simulez la rentabilité locative d'un investissement immobilier : rendement, cash-flow et fiscalité.",
  },
  "/boite-a-outils/estimer-un-loyer": {
    description:
      "Estimez le loyer de marché d'un bien selon son adresse et ses caractéristiques.",
  },
  "/boite-a-outils/home-staging": {
    description: "Meublez, modernisez ou personnalisez une pièce à partir des photos d’un bien avec le home staging virtuel AnyHomes.",
  },
  "/boite-a-outils/travaux": {
    description:
      "Estimez le coût de vos travaux de rénovation avant d'acheter ou de vendre.",
  },
  "/boite-a-outils/credit": {
    description:
      "Simulez votre crédit immobilier : mensualités, coût total et capacité d'emprunt.",
  },
  "/boite-a-outils/fiscalite-revente": {
    description:
      "Calculez la fiscalité de revente d'un bien immobilier et anticipez vos plus-values.",
  },
  "/boite-a-outils/comprendre-une-ville": {
    description:
      "Découvrez une ville avant d'y acheter : prix, transports, écoles, qualité de vie et données locales.",
  },
  "/past-transation-list": {
    description:
      "Consultez l'historique des transactions immobilières récentes et les prix de vente par secteur.",
  },
  "/building-permit-list": {
    description:
      "Consultez les permis de construire délivrés autour d'une adresse et anticipez les projets du quartier.",
  },
  "/company-details": {
    description:
      "Consultez la fiche d'un professionnel de l'immobilier sur AnyHomes : coordonnées, spécialités et services proposés.",
  },
};

/** Préfixes de routes nécessitant une session authentifiée (hors mode invité). */
const PROTECTED_ROUTE_PREFIXES = [
  "/dashboard",
  "/my-properties",
  "/chat",
  "/profile",
  "/billing",
  "/billing-history",
  "/agent-autonome",
  "/assistant-candidat",
  "/transaction-dashboard",
  "/seller-file",
  "/pro/",
  "/agency/",
  "/notifications",
  "/project",
  "/settings",
  "/referral-admin",
  "/admin/referrals",
  // Création / modification d'un bien : réservé aux utilisateurs connectés.
  "/property1",
  "/property3",
  "/property/add",
  "/property/edit",
];

const PageRouter = ({ children }: any) => {
  const user = useSelector((state: any) => state.user);
  const location = useLocation();
  const loggedIn = !!user?.loggedIn && !user?.isGuest;
  const guest = !!user?.isGuest || isGuestMode();
  const pathname = location.pathname;
  const needsAuth = PROTECTED_ROUTE_PREFIXES.some(
    (p) => pathname === p || pathname.startsWith(p)
  );

  // Garde d'auth : une route protégée sans session (hors mode invité) renvoie
  // vers la connexion.
  if (needsAuth && !loggedIn && !guest) {
    return <Navigate to="/login" replace state={{ from: pathname + location.search }} />;
  }

  return <ErrorBoundary>{children}</ErrorBoundary>;
};

/**
 * Remonte la page à chaque navigation (chemin + query string).
 *
 * Sans cela, une route réutilisée (ex. `/property-details?id=A` → `?id=B`, ou un
 * écran déjà visité) conserve son instance React : les `useEffect` de montage ne
 * se rejouent pas et l'écran affiche un état figé en mémoire au lieu de recharger
 * ses données.
 */
const RouteKeyed = ({ children }: any) => {
  const location = useLocation();
  return (
    <Fragment key={`${location.pathname}${location.search}`}>
      {children}
    </Fragment>
  );
};

/** Fallback de navigation : évite l'écran blanc pendant le chargement d'un chunk. */
const RouteFallback = () => (
  <div className="flex items-center justify-center w-full py-24">
    <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[var(--brand,#976DD0)]" />
  </div>
);

/**
 * Routes qui ne doivent PAS être encapsulées dans le PageLayout racine
 * (elles ont leur propre layout — marketing/auth — ou pas de chrome).
 * Prefix match : `/signup` couvre aussi `/signup/pro`.
 */
const NO_CHROME_ROUTES = [
  "/agency/",
  "/login",
  "/signup",
  "/forgotpassword",
  "/reset-password",
  "/otpverify",
  "/activation-invitation",
  "/impersonate",
  "/change-password",
  "/reset-email",
  "/reset-new-email",
  "/phone-number",
  "/proprietaire",
  "/acheter-avec-anyhomes",
  "/vendre-avec-anyhomes",
  "/transactions-historiques",
  "/agent-immo-demande",
  "/estimation-entre-particuliers",
  "/parcours-immo-digital",
  "/assistants-immo-ia",
  "/vos-reperes-immobiliers",
  "/outil-transactionnel",
  "/vente-off-market",
  "/services-a-la-carte",
  "/profil-bien",
  "/fonctionnalites",
  "/parrainage",
  "/parrainage-cgu",
  "/cgu-cgv",
  "/privacy-policy",
  "/pro-offers",
  "/pro-offers/agents",
  "/pro-offers/hunters",
  "/programme-pilote",
  "/pro/stripe/success",
  "/pro/stripe/refresh",
  "/app-onboarding",
  "/app-assistant",
  "/retour-experience",
  "/experiences/",
];

/**
 * Routes MARKETING : en app native, elles sont redirigées vers l'AppHome
 * (l'app n'affiche pas l'environnement marketing du web). Le web n'est pas
 * impacté (gating `isApp()`). Les pages légales (CGU/Privacy) et les pages
 * « app » (annonces, recherche, marketplace, profil, boîte à outils…) ne sont
 * PAS dans cette liste.
 */
const MARKETING_ROUTES = [
  "/proprietaire",
  "/acheter-avec-anyhomes",
  "/vendre-avec-anyhomes",
  "/home-vendeurs",
  "/transactions-historiques",
  "/agent-immo-demande",
  "/estimation-entre-particuliers",
  "/parcours-immo-digital",
  "/outil-transactionnel",
  "/assistants-immo-ia",
  "/vos-reperes-immobiliers",
  "/vente-off-market",
  "/services-a-la-carte",
  "/profil-bien",
  "/fonctionnalites",
  "/parrainage",
  "/pro-offers",
  "/pro-offers/agents",
  "/pro-offers/hunters",
  "/programme-pilote",
  "/offers-pro",
  "/offers-pro-agent",
  "/offers-pro-hunter",
  "/directory",
  "/offmarket",
  "/transaction-tool",
  "/blogs",
  "/blog-detail",
  "/blog-owning",
  "/blog-own-detail",
  "/video-own-detail",
];

/**
 * Shell qui hoist le PageLayout racine au-dessus de <Routes> afin que la
 * sidebar et le header ne soient PAS démontés/remontés à chaque navigation
 * (sinon : clignotement complet du chrome à chaque clic sidebar).
 * - Routes auth et home marketing `/` (exact) → pas de PageLayout racine
 * - Toutes les autres routes → wrap dans <PageLayout>. Les pages qui se
 *   wrappent elles-mêmes dans <PageLayout> détectent le contexte parent
 *   et passent en mode passthrough (voir PageLayoutContext).
 */
const RouteLayoutShell = ({ children }: any) => {
  const location = useLocation();
  const pathname = location?.pathname || "";
  const { agencyTheme } = useWhiteLabel();
  const agencyName = agencyTheme?.agencyName || "";
  useEffect(() => {
    // Analytics : une « page view » (PostHog $pageview) par route.
    trackPageView(window.location.href);
  }, [pathname, location.search]);

  useEffect(() => {
    // Conservation du mode inapp pour les navigations internes (ex. /parrainage).
    const q = new URLSearchParams(location.search);
    if (q.get("isApp") === "1" || q.get("IsApp") === "1") {
      try { sessionStorage.setItem("isAppView", "1"); } catch (e) { /* ignore */ }
    }
  }, [location.search]);

  useEffect(() => {
    const hasWhiteLabel = document.documentElement.style.getPropertyValue('--sidebar-bg');
    const pageContent = (PAGE_TITLES[pathname] || "").replace(/\s*-\s*AnyHomes\s*$/i, "").trim();
    let finalTitle: string;
    if (hasWhiteLabel && agencyName) {
      finalTitle = pageContent ? `${pageContent} - ${agencyName}` : agencyName;
    } else {
      finalTitle = pageContent ? `${pageContent} - AnyHomes` : "AnyHomes";
    }
    const pageSeo = PAGE_SEO[pathname] || {};
    try {
      applySeo({
        title: finalTitle,
        description: pageSeo.description,
        type: pageSeo.type || "website",
        url: pathname,
      });
    } catch (e) {
      // SEO non bloquant : ne doit jamais casser la navigation
    }
  }, [pathname, agencyName]);
  const isHomeMarketing = pathname === "/" || pathname === "/home-acheteurs" || pathname === "/home-vendeurs";
  // Galerie photos d'une annonce (liste + plein écran) : écran dédié sans
  // sidebar/header/bottom-nav, y compris sur iPad.
  const isPhotoGalleryRoute = /^\/annonce\/[^/]+\/(photos|photo\/[^/]+)\/?$/.test(pathname);
  const isNoChromeRoute = isPhotoGalleryRoute || NO_CHROME_ROUTES.some((p) => pathname.startsWith(p));
  const isEmbed = new URLSearchParams(location.search).get("embed") === "true";
  // Vue inapp (app mobile) : /parrainage s'affiche AVEC le chrome (sidebar + header inapp).
  const isAppView =
    new URLSearchParams(location.search).get("isApp") === "1" ||
    new URLSearchParams(location.search).get("IsApp") === "1" ||
    (typeof sessionStorage !== "undefined" && sessionStorage.getItem("isAppView") === "1");
  const inAppParrainageOverride =
    (isAppView || isGuestMode()) && (pathname === "/parrainage" || pathname.startsWith("/parrainage"));
  // Parcours de création / modification d'un bien : on masque l'assistant IA.
  // Son avatar flottant recouvrait des boutons (ex. « Passer » de l'étape
  // off-market) — aucun accès à l'assistant pendant la création/édition.
  const isPropertyEditorRoute =
    pathname === "/property1" ||
    pathname === "/property3" ||
    pathname.startsWith("/property/add") ||
    pathname.startsWith("/property/edit");
  // Espace profil : on masque l'avatar de l'assistant IA (interface dédiée).
  const isProfileRoute = pathname === "/profile" || pathname.startsWith("/profile/");
  // Messagerie : on masque l'avatar flottant de l'assistant IA (il gêne la
  // zone de conversation en bas à droite).
  const isChatRoute = pathname === "/chat" || pathname.startsWith("/chat/");
  const isEstimationRoute = pathname === "/estimation";

  // App native : pas d'environnement marketing. `/` et les routes marketing
  // sont redirigées vers l'AppHome. Le web n'est PAS impacté (gating isApp()).
  const inApp = isApp();
  // Web sur téléphone (hors app native) : on masque l'avatar flottant de
  // l'assistant IA (il gêne sur petit écran).
  const [isMobileViewport, setIsMobileViewport] = useState(
    () => typeof window !== "undefined" && window.innerWidth < 768
  );
  useEffect(() => {
    const onResize = () => setIsMobileViewport(window.innerWidth < 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  const isWebMobile = !inApp && isMobileViewport;
  const isMarketingRoute =
    pathname === "/" ||
    MARKETING_ROUTES.some((p) => pathname === p || pathname.startsWith(p));
  if (inApp && isMarketingRoute) {
    return <Navigate to="/app-home" replace />;
  }

  // App native : onboarding de 1er lancement (une seule fois), sur l'accueil.
  const appOnboardingPending =
    inApp &&
    typeof localStorage !== "undefined" &&
    localStorage.getItem("app_onboarding_done") !== "1";
  if (inApp && appOnboardingPending && (pathname === "/app-home" || pathname === "/")) {
    return <Navigate to="/app-onboarding" replace />;
  }

  // Le bandeau de preuve sociale n'a pas sa place sur l'écran d'estimation :
  // il recouvre les zones d'information et de formulaire.
  const hideSocialProof = pathname === "/estimation";

  if (isHomeMarketing || (isNoChromeRoute && !inAppParrainageOverride) || isEmbed) {
    return (
      <>
        {!hideSocialProof && <Suspense fallback={null}><SocialProofNotification inApp={false} /></Suspense>}
        {children}
      </>
    );
  }
  return (
    <PageLayout>
      {!hideSocialProof && <Suspense fallback={null}><SocialProofNotification inApp={true} /></Suspense>}
      {/* Assistant Immobilier IA — launcher global de l'espace connecté.
          Routes des capacités via /assistant/chat + /assistant/history ;
          contexte outil si l'URL est /agent-autonome* / /assistant-candidat*.
          Masqué pendant la création/modification d'un bien, sur l'espace profil
          et sur la messagerie. */}
      {!inApp && !isWebMobile && !isPropertyEditorRoute && !isProfileRoute && !isChatRoute && !isEstimationRoute && (
        <Suspense fallback={null}><AgentImmoAssistant /></Suspense>
      )}
      {children}
    </PageLayout>
  );
};

function App() {
  const [isGuestApp, setIsGuestApp] = useState(isGuestMode());
  const [subdomainSlug, setSubdomainSlug] = useState('');

  // Subdomain detection — stocke le slug sans redirection
  useEffect(() => {
    const hostname = window.location.hostname;
    const parts = hostname.split('.');
    let slug = null;
    if (parts.length >= 3 && parts[0] !== 'www') {
      slug = parts[0];
    } else if (parts.length === 2 && parts[1] === 'localhost' && parts[0] !== 'www') {
      slug = parts[0];
    }
    if (slug) {
      setWhiteLabelSlug(slug);
      setSubdomainSlug(slug);
    }
  }, []);

  useEffect(() => {
    const updateGuestMode = () => setIsGuestApp(isGuestMode());
    window.addEventListener('guestModeChanged', updateGuestMode);
    return () => window.removeEventListener('guestModeChanged', updateGuestMode);
  }, []);

  // Add `is-mounted` to body as soon as App mounts to enable guarded transitions.
  // IMPORTANT : on ne retire JAMAIS ces classes de chrome. Le passage
  // invité → connecté (login SPA sans rechargement) ne rejoue pas le
  // PersistGate ; retirer `is-rehydrated`/`is-stable` laissait alors la sidebar
  // masquée (bandeau blanc) et coupait toutes les animations (toast figé).
  useEffect(() => {
    try { document.body.classList.add('is-mounted'); } catch (e) {}
  }, []);

  // En debug/mock ou en mode invité il n'y a pas d'étape de rehydratation
  // PersistGate : on marque le store comme rehydraté pour rendre l'UI visible.
  // Une fois ajoutées, ces classes restent (voir note ci-dessus).
  useEffect(() => {
    try {
      if (isDebugMockUser() || isGuestApp) {
        document.body.classList.add('is-rehydrated');
        document.body.classList.add('is-stable');
      }
    } catch (e) {}
  }, [isGuestApp]);

  // Dev helper: observe DOM mutations (classes/attributes/children) to
  // detect which elements change during mount/hydration and cause flicker.
  useEffect(() => {
    if (process.env.NODE_ENV === 'production') return;
    let observer: MutationObserver | null = null;
    try {
      const selectors = [
        // Do NOT include sidebar/header — their class changes on navigation
        // (active link classes) would trigger is-stable removal and cause flicker.
        // Only watch the page content area for DOM stability.
        '.page-content-wrapper'
      ];
      // Elements/areas to ignore (Leaflet map tiles and panes cause many mutations)
      const ignoreSelectors = ['.leaflet-container', '.leaflet-tile', '.leaflet-pane', '.leaflet-tile-container'];
      console.log('[DOM-MUTATION INIT] attaching global observer');
      let stableTimer: number | null = null;
      const setStable = () => {
        try { document.body.classList.add('is-stable'); } catch (e) {}
      };
      const clearStable = () => {
        try { document.body.classList.remove('is-stable'); } catch (e) {}
      };

      const scheduleStable = () => {
        if (stableTimer) window.clearTimeout(stableTimer);
        clearStable();
        stableTimer = window.setTimeout(() => {
          setStable();
          stableTimer = null;
        }, 200);
      };

      observer = new MutationObserver((mutations: MutationRecord[]) => {
        mutations.forEach((m: MutationRecord) => {
          const target = m.target as Element;
          const matchesSelector = (el: Element | null) => {
                if (!el) return false;
                try {
                  // ignore mutations originating inside ignored selectors
                  for (const ign of ignoreSelectors) {
                    if (el.closest && el.closest(ign)) return false;
                  }
                } catch (e) {}
                return selectors.some(sel => {
                  try { return (el as Element).matches(sel) || (el.closest && el.closest(sel) != null); } catch (e) { return false; }
                });
              };

          if (m.type === 'attributes' && m.attributeName === 'class') {
            if (matchesSelector(target) || matchesSelector(target.parentElement)) {
              const className = (target as HTMLElement).className || '';
              console.log('[DOM-MUTATION] class change:', target, 'new=', className);
              scheduleStable();
            }
          }
          if (m.type === 'childList') {
            let relevant = false;
            if (m.addedNodes.length) {
              Array.from(m.addedNodes).forEach(n => {
                if (n.nodeType === 1) {
                  const el = n as Element;
                  // ignore nodes inside ignored selectors
                  let ignored = false;
                  try { for (const ign of ignoreSelectors) { if (el.closest && el.closest(ign)) { ignored = true; break; } } } catch (e) {}
                  if (ignored) return;
                  if (matchesSelector(el)) {
                    console.log('[DOM-MUTATION] node added', n);
                    relevant = true;
                  }
                }
              });
            }
            if (m.removedNodes.length) {
              Array.from(m.removedNodes).forEach(n => {
                if (n.nodeType === 1) {
                  const el = n as Element;
                  let ignored = false;
                  try { for (const ign of ignoreSelectors) { if (el.closest && el.closest(ign)) { ignored = true; break; } } } catch (e) {}
                  if (ignored) return;
                  if (matchesSelector(el)) {
                    console.log('[DOM-MUTATION] node removed', n);
                    relevant = true;
                  }
                }
              });
            }
            if (relevant) scheduleStable();
          }
        });
      });
      observer.observe(document.body, { attributes: true, childList: true, subtree: true });
      // If no DOM mutations happen after mount, still consider the app stable
      // so header/navigation elements become visible.
      scheduleStable();
    } catch (e) {
      // ignore
    }
    return () => { if (observer) observer.disconnect(); };
  }, []);
  // const { t } = useTranslation();
  const routes = [
    { url: "*", path: "NotFoundPage" }, // Not Found Page
    { url: "/services-a-la-carte", path: "ServicesCarte" },
    // Boîte à outils (InApp) — grille d'apps + Estimateur de bien
    { url: "/boite-a-outils", path: "BoiteAOutils" },
    { url: "/boite-a-outils/estimateur", path: "BoiteAOutils/Estimateur" },
    { url: "/boite-a-outils/estimateur/demarrer", path: "BoiteAOutils/Estimateur/Wizard" },
    { url: "/boite-a-outils/taxe-fonciere", path: "BoiteAOutils/TaxeFonciere" },
    { url: "/boite-a-outils/carte-scolaire", path: "BoiteAOutils/CarteScolaire" },
    { url: "/boite-a-outils/carte-scolaire/college/:uai", path: "BoiteAOutils/CarteScolaire/College" },
    { url: "/boite-a-outils/prix-marche", path: "BoiteAOutils/PrixMarche" },
    { url: "/boite-a-outils/frais-notaire", path: "BoiteAOutils/FraisNotaire" },
    { url: "/boite-a-outils/taxe-habitation", path: "BoiteAOutils/TaxeHabitation" },
    { url: "/boite-a-outils/applications", path: "BoiteAOutils/Applications" },
    { url: "/boite-a-outils/mes-simulations", path: "BoiteAOutils/MesSimulations" },
    { url: "/boite-a-outils/mes-simulations/homestaging/:propertyId", path: "BoiteAOutils/HomeStaging/SimulationResult" },
    { url: "/boite-a-outils/mes-simulations/:id", path: "BoiteAOutils/SimulationResult" },
    { url: "/boite-a-outils/rentabilite", path: "BoiteAOutils/Rentabilite" },
    { url: "/boite-a-outils/rentabilite/demarrer", path: "BoiteAOutils/Rentabilite/Wizard" },
    { url: "/boite-a-outils/rentabilite/:id", path: "BoiteAOutils/Rentabilite/SimulationDetail" },
    { url: "/boite-a-outils/mes-simulations-investissement", path: "BoiteAOutils/Rentabilite/MesSimulations" },
    { url: "/boite-a-outils/credit", path: "BoiteAOutils/Credit" },
    { url: "/boite-a-outils/estimer-un-loyer", path: "BoiteAOutils/EstimateurLoyer" },
    { url: "/boite-a-outils/estimer-un-loyer/demarrer", path: "BoiteAOutils/EstimateurLoyer/Wizard" },
    { url: "/boite-a-outils/estimer-un-loyer/resultat/:id", path: "BoiteAOutils/EstimateurLoyer/EstimateDetail" },
    { url: "/boite-a-outils/mes-estimations-loyer", path: "BoiteAOutils/EstimateurLoyer/MesEstimations" },
    { url: "/boite-a-outils/fiscalite-revente", path: "BoiteAOutils/FiscaliteRevente" },
    { url: "/boite-a-outils/fiscalite-revente/demarrer", path: "BoiteAOutils/FiscaliteRevente/Wizard" },
    { url: "/boite-a-outils/fiscalite-revente/:id", path: "BoiteAOutils/FiscaliteRevente/SimulationDetail" },
    { url: "/boite-a-outils/mes-simulations-fiscalite", path: "BoiteAOutils/FiscaliteRevente/MesSimulations" },
    { url: "/boite-a-outils/comprendre-une-ville", path: "BoiteAOutils/FicheVille" },
    { url: "/boite-a-outils/comprendre-une-ville/:insee", path: "BoiteAOutils/FicheVille/CityProfile" },
    { url: "/boite-a-outils/connaitre-le-dpe", path: "BoiteAOutils/Dpe" },
    { url: "/boite-a-outils/analyser-un-dpe", path: "BoiteAOutils/AnalyserDpe" },
    { url: "/boite-a-outils/travaux", path: "BoiteAOutils/Travaux" },
    { url: "/boite-a-outils/home-staging", path: "BoiteAOutils/HomeStaging" },
    { url: "/boite-a-outils/financabilite", path: "BoiteAOutils/Financabilite" },
    { url: "/boite-a-outils/budget-occupation", path: "BoiteAOutils/BudgetOccupation" },
    { url: "/boite-a-outils/demenagement", path: "BoiteAOutils/Demenagement" },
    { url: "/boite-a-outils/prix-maximal-offre", path: "BoiteAOutils/PrixMaximalOffre" },
    { url: "/boite-a-outils/acheter-avant-de-vendre", path: "BoiteAOutils/AcheterAvantDeVendre" },
    { url: "/boite-a-outils/acheter-ou-rester-locataire", path: "BoiteAOutils/AcheterOuResterLocataire" },
    { url: "/home-acheteurs", path: "HomeBuyers" },
    { url: "/home-vendeurs", path: "HomeSellers" },
    { url: "/", path: subdomainSlug ? "AgencyLanding" : "HomeBuyers" },
    // Home de l'app native (Capacitor) — l'environnement marketing n'est pas affiché.
    { url: "/app-home", path: "AppHome" },
    { url: "/app-onboarding", path: "AppOnboarding" },
    { url: "/app-assistant", path: "AppAssistant" },
    { url: "/home-legacy", path: "Home" }, // Ancienne home (sauvegarde, à retirer plus tard)
    { url: "/signup", path: "AuthPage" }, // Auth Page Routes
    { url: "/signup/pro", path: "AuthPage" }, // Auth Page Routes
    { url: "/activation-pro/:token", path: "DormantActivation" }, // Activation annonceur pro dormant
    { url: "/change-password", path: "ChangePassword" }, // Auth Page Routes
    { url: "/login", path: "AuthPage" },
    { url: "/onboarding", path: "Onboarding" },
    { url: "/otpverify", path: "Otpverify" },
    { url: "/activation-invitation/:token", path: "ActivationInvitation" },
    { url: "/impersonate", path: "Impersonate" },
    { url: "/finalize", path: "Finalize" },
    { url: "/reset-email", path: "ResetEmail" },
    { url: "/reset-new-email", path: "ResetNewEmail" },
    { url: "/forgotpassword", path: "Forgotpassword" },
    { url: "/reset-password", path: "Resetpassword" },
    { url: "/phone-number", path: "PhoneNumber" },
    { url: "/help", path: "Help" },
    { url: "/profile", path: "Settings/ProfilePage" },
    { url: "/profile/company-logo", element: <Navigate to="/profile" state={{ scrollTo: 'company-logo' }} /> },
    // Preserve old URL by redirecting to the main profile settings page
    { url: "/profile/company-details", element: <Navigate to="/profile" /> },
    { url: "/profile/contact-details", element: <Navigate to="/profile" state={{ scrollTo: 'contact-details' }} /> },
    { url: "/profile/about", element: <Navigate to="/profile" state={{ scrollTo: 'about-company' }} /> },
    { url: "/profile/team", element: <Navigate to="/profile" state={{ scrollTo: 'team' }} /> },
    { url: "/profile/services", element: <Navigate to="/profile" state={{ scrollTo: 'services' }} /> },
    { url: "/settings/work-hour", element: <Navigate to="/profile" state={{ scrollTo: 'opening-hours' }} /> },
    {
      url: "/profile/manage-notifications",
      path: "Profile/ManageNotifications",
    },
    { url: "/profile/:tab", path: "Profile" },
    { url: "/properties", path: "Property" },
    { url: "/mes-recherches/publier", path: "SearchListings/PublishSearch" },
    { url: "/mes-recherches/:id/modifier", path: "SearchListings/EditSearch" },
    { url: "/mes-recherches/:id", path: "SearchListings/Detail" },
    { url: "/mes-recherches", path: "SearchListings/Mine" },
    { url: "/recherches/:id", path: "SearchListings/PublicDetail" },
    { url: "/recherches", path: "SearchListings/Discover" },
    { url: "/property1", path: "propertySteps/property1" },
    { url: "/property3", path: "propertySteps/property3" },
    { url: "/property/add", path: "propertySteps/AddEdit" },
    { url: "/property/edit/:id", path: "propertySteps/Edit" },
    { url: "/property/edit/:id/:step", path: "propertySteps/Edit" },
    { url: "/property/add/:step", path: "propertySteps/AddEdit" },
    // QR Code management page
    { url: "/property/qr-code", path: "QRCodeManagement" },
    { url: "/project", path: "Project" },
    { url: "/dashboard", path: "Dashboard" },
    { url: "/retour-experience", path: "FeedbackExperience" },
    { url: "/experiences/validation/:token", path: "ExperienceApproval" },
    { url: "/experiences/:slug", path: "ExperiencePublic" },
    { url: "/property-details", path: "PropertyDetails" },
    { url: "/property-details/analyse-rentabilite", path: "PropertyDetails/RentabilityAnalysis" },
    // URL sémantique SEO : /annonce/titre-du-bien-<id>
    { url: "/annonce/:slug/photos", path: "PropertyDetails/Gallery" },
    { url: "/annonce/:slug/photo/:index", path: "PropertyDetails/PhotoViewer" },
    { url: "/annonce/:slug", path: "PropertyDetailsSlug" },
    { url: "/property-timeline", path: "PropertyTimeline" },
    { url: "/serach-alert", path: "SearchAlert" },
    { url: "/buyer-file/synthese/:propertyId", path: "BuyerFile/CreateSynthesis" },
    { url: "/buyer-file", path: "BuyerFile" },
    { url: "/renter-file/synthese/:propertyId", path: "BuyerFile/CreateSynthesis" },
    { url: "/renter-file", path: "RenterFile" },
    { url: "/seller-file", path: "SellerFile" },
    { url: "/followed-properties", path: "FollowedProperty" },
    { url: "/followed-properties-list", path: "FollowedPropertyList" },
    { url: "/notifications", path: "Notifications" },
    { url: "/chat", path: "Chat" },
    { url: "/building-permit", path: "BuildingPermit" },
    { url: "/building-permit-list", path: "BuildingPermitlist" },
    { url: "/past-transactions", path: "PastTransactions" },
    { url: "/past-transation-list", path: "PastTransectionList" },
    { url: "/real-estate-pros", path: "RealEstatePros" },
    { url: "/marketplace", path: "Marketplace" },
    { url: "/marketplace/favorites", path: "Marketplace/Favorites" },
    { url: "/marketplace/:id", path: "Marketplace/ServiceDetail" },
    { url: "/marketplace/orders", path: "MarketplaceOrders" },
    { url: "/pro/marketplace", path: "ProMarketplace" },
    { url: "/pro/marketplace/sold-services", path: "ProMarketplace/SoldServices" },
    { url: "/pro/marketplace/services/new", path: "ProMarketplace/ServiceEditor" },
    { url: "/pro/marketplace/services/:id/edit", path: "ProMarketplace/ServiceEditor" },
    { url: "/pro/marketplace/catalogue", path: "ProMarketplace/Catalogue" },
    { url: "/pro/marketplace/catalogue/:id", path: "ProMarketplace/CatalogueDetail" },
    { url: "/pro/portfolio", path: "ProPortfolio" },
    { url: "/pro/portfolio/nouveau", path: "ProPortfolio/Editor" },
    { url: "/pro/portfolio/:id/modifier", path: "ProPortfolio/Editor" },
    { url: "/pro/portfolio/:id/apercu", path: "ProPortfolio/Preview" },
    { url: "/pro/payouts", path: "ProPayouts" },
    { url: "/pro/configure-payments", path: "ProConfigurePayments" },
    { url: "/pro/stripe/success", path: "StripeOnboarding/Success" },
    { url: "/pro/stripe/refresh", path: "StripeOnboarding/Refresh" },
    { url: "/pro-offers", path: "ProOffers" },
    { url: "/pro-offers/agents", path: "ProOffersAgents" },
    { url: "/pro-offers/hunters", path: "ProOffersHunters" },
    { url: "/pro-offers/diagnostiqueurs", path: "ProOffersDiagnostiqueurs" },
    { url: "/pro-offers/decorateurs", path: "ProOffersDecorateurs" },
    { url: "/programme-pilote", path: "ProgrammePilote" },
    { url: "/pro-offers/investor", path: "ProOffersInvestor" },
    { url: "/offers-pro", path: "OffersPro" },
    { url: "/offers-pro-agent", path: "OffersProAgents" },
    { url: "/offers-pro-hunter", path: "OffersProHunters" },
    { url: "/offers-pro-diagnostiqueur", path: "OffersProDiagnostiqueurs" },
    { url: "/offers-pro-decorateur", path: "OffersProDecorateurs" },
    { url: "/offers-pro-investor", path: "OffersProInvestor" },
    { url: "/revons-plus-grand", path: "DreamBigger" },
    { url: "/revons-plus-grand/resultats", path: "DreamBigger" },
    { url: "/prolist", path: "Prolist" },
    { url: "/professionnel/:slug", path: "CompanyDetails" },
    { url: "/company-details", path: "CompanyDetails" },
    { url: "/portfolio/:proId/:projectId", path: "Portfolio/Project" },
    { url: "/blogs", path: "Blogs" },
    { url: "/blog-detail", path: "BlogDetail" },
    { url: "/blog-owning", path: "Blogs/blogOwning" },
    { url: "/blog-own-detail", path: "Blogs/blogOwnDetail" },
    { url: "/video-own-detail", path: "Blogs/videoOwnDetail" },
    { url: "/my-properties", path: "Property/MyProperties" },
    { url: "/contact-us", path: "ContactUs" },
    { url: "/plan", path: "Plan" },
    {
      url: "/real-estate-transaction-owner",
      path: "RealEstateTransactionOwner",
    },
    {
      url: "/real-estate-transaction-searcher",
      path: "RealEstateTransactionSearcher",
    },
    { url: "/card-detail", path: "CardDetail" },
    { url: "/billing-history", path: "BillingHistory" },
    { url: "/upgrade-plan", path: "UpgradePlan" },
    { url: "/billing", path: "Billing" },
    { url: "/transaction1", path: "Transaction" },
    { url: "/transaction2", path: "Transaction/Transaction2" },
    { url: "/transaction3", path: "Transaction/Transaction3" },
    { url: "/transaction4", path: "Transaction/Transaction4" },
    { url: "/transaction-dashboard", path: "TransactionDashboard" },
    { url: "/dossier-visite/generer/:propertyId", path: "VisitFolderDashboard/GeneratePage" },
    { url: "/dossier-visite", path: "VisitFolderDashboard" },
    { url: "/coach-immo", path: "CoachImmoIA" },
    { url: "/agent-autonome", path: "AgentAutonome" },
    { url: "/agent-autonome/documents", path: "AssistantDocuments" },
    { url: "/assistants-immo-ia", path: "AssistantsImmoIA" },
    { url: "/assistants-immo-ia-inapp", path: "AssistantsImmoIA/InApp" },
    { url: "/vos-reperes-immobiliers", path: "RepereMarketing" },
    { url: "/agent-autonome/assign", path: "AgentAutonome/ConfigureAgent" },
    { url: "/agent-autonome/configure/:propertyId", path: "AgentAutonome/ConfigureAgent" },
    { url: "/agent-autonome/property/:propertyId", path: "AgentAutonome/AgentPropertyDetail" },
    { url: "/learning-center", path: "training" },
    { url: "/pro-learning", path: "ProLearning" },
    { url: "/pro-learning/video", path: "ProLearning/VideoNew" },
    { url: "/pro-learning/article", path: "ProLearning/ArticleNew" },
    { url: "/pro-learning/edit/:contentType/:id", path: "ProLearning/ArticleNew" },
    { url: "/invite", path: "Invite" },
    { url: "/peertopeer", path: "PeertopeerEstimation/peertopeer" },
    { url: "/estimation", path: "PeertopeerEstimation/estimation" },
    { url: "/experience", path: "PeertopeerEstimation/experience" },
    { url: "/alert", path: "PeertopeerEstimation/alert" },
    { url: "/wallet", path: "PeertopeerEstimation/wallet" },
    { url: "/social-estimation", path: "PeertopeerEstimation/socialEstimation" },
    { url: "/hunter-form", path: "Blogs/forms/hunterForm" },
    { url: "/selling-form", path: "Blogs/forms/sellingForm" },
    { url: "/interest-form", path: "Blogs/forms/interestForm" },
    { url: "/getquote-form", path: "Blogs/forms/getQuoteForm" },
    { url: "/getmove-form", path: "Blogs/forms/moveForm" },
    { url: "/directory", path: "LandingPage/directory" },
    { url: "/offmarket", path: "LandingPage/offmarket" },
    { url: "/transaction-tool", path: "LandingPage/transactionTool" },
    { url: "/transactions-historiques", path: "TransactionHistoryMarketing" },
    { url: "/agent-immo-demande", path: "OnDemandAgent" },
    { url: "/estimation-entre-particuliers", path: "PeertopeerEstimation" },
    { url: "/parcours-immo-digital", path: "TransactionTool" },
    { url: "/assistant-candidat/config/:alertId", path: "AssistantCandidat/Config" },
    { url: "/assistant-candidat/pilot/:alertId", path: "AssistantCandidat/Pilot" },
    { url: "/assistant-candidat/assign", path: "AssistantCandidat/AssignCandidate" },
    { url: "/assistant-candidat", path: "AssistantCandidat" },
    { url: "/assistant-candidat/documents", path: "AssistantDocuments" },
    { url: "/outil-transactionnel", element: <Navigate to="/parcours-immo-digital" replace /> },
    { url: "/vente-off-market", path: "OffMarket" },
    { url: "/profil-bien", path: "PropertyProfile" },
    { url: "/fonctionnalites", path: "Fonctionnalites" },
    { url: "/proprietaire", path: "OwnerLanding" },
    { url: "/acheter-avec-anyhomes", path: "BuyerLanding" },
    { url: "/vendre-avec-anyhomes", path: "SellerLanding" },
    { url: "/parrainage", path: "ReferralLanding" },
    { url: "/parrainage-cgu", path: "ReferralCgu" },
    { url: "/admin/referrals", path: "ReferralAdminDashboard" },
    { url: "/cgu-cgv", path: "CguCgv" },
    { url: "/support-page", path: "Support" },
    { url: "/privacy-policy", path: "PrivacyPolicy" },
    { url: "/delete-user", path: "DeleteUser" },
    { url: "/invite/parrainage", path: "Invite/ReferralProgramDetail" },
    { url: "/invite/:token", path: "Invite/Accept" },
    // White-label (Marque Blanche) — espace agence
    { url: "/agency/dashboard", path: "Agency/Dashboard" },
    { url: "/agency/settings", path: "Agency/Settings" },
    { url: "/agency/leads", path: "Agency/Leads" },
    { url: "/agency/leads/detail/:id", path: "Agency/UserDetail" },
    { url: "/agency/leads/edit/:id", path: "Agency/UserEdit" },
    { url: "/agency/properties", path: "Agency/Properties" },
    { url: "/agency/properties/detail/:id", path: "Agency/PropertyDetail" },
    { url: "/agency/hot-leads", path: "Agency/HotLeads" },
    { url: "/agency/collaborators", path: "Agency/Team" },
    { url: "/agency/collaborators/:userId", path: "Agency/Member" },
    { url: "/agency/join", path: "Agency/Join" },
    { url: "/agency/marketplace-sold-services", path: "Agency/MarketplaceSoldServices" },
    // Page vitrine agence
    { url: "/agency/:slug", path: "AgencyLanding" },
    // Votre agence (pour user white-label)
    { url: "/my-agency", path: "MyAgency" },
  ];

  // Cache stable des composants lazy : évite que chaque navigation
  // recrée un nouveau composant et fasse apparaître le fallback Suspense
  // (effet "toute l'app se recharge").
  const routesWithElements = useMemo(
    () => routes.map((itm: any) => ({
      ...itm,
      Element: itm.path && pageImports[itm.path] ? lazy(pageImports[itm.path]) : null,
    })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [subdomainSlug]
  );

  return (
    <>
      <Provider store={store}>
        {/* ── Coach IA Phase 2: WebSocket Real-Time Provider ── */}
        <CoachWebSocketProvider>
          <WhiteLabelProvider>
          {isDebugMockUser() || isGuestApp ? (
            <Suspense
              fallback={
                <div id="loader" className="loaderDiv">
                  <div>
                    <img
                      src="/assets/img/loader.gif"
                      alt="logo"
                      className="loaderlogo"
                    />
                  </div>
                </div>
              }
            >
              <Router>
                <RouteLayoutShell>
                  <Routes>
                  {routesWithElements.map((itm: any, index) => {
                    const Element = itm.Element;
                    return (
                      <Route
                        path={itm.url}
                        key={index}
                        element={
                          itm.path ? (
                            <Suspense fallback={<RouteFallback />}>
                              <PageRouter
                                url={itm.url}
                                auth={itm.auth ? true : false}
                              >
                                <RouteKeyed>
                                  <Element />
                                </RouteKeyed>
                              </PageRouter>
                            </Suspense>
                          ) : (
                            itm.element
                          )
                        }
                      />
                    );
                  })}
                  </Routes>
                </RouteLayoutShell>
                <ExplainerManager />
                <ScrollRestoration />
              </Router>
            </Suspense>
          ) : (
            <PersistGate loading={"loading ..."} persistor={persistor} onBeforeLift={() => {
              try { document.body.classList.add('is-rehydrated'); document.body.classList.add('is-stable'); } catch (e) {}
            }}>
              <Suspense
                fallback={
                  <div id="loader" className="loaderDiv">
                    <div>
                      <img
                        src="/assets/img/loader.gif"
                        alt="logo"
                        className="loaderlogo"
                      />
                    </div>
                  </div>
                }
              >
                <Router>
                  <RouteLayoutShell>
                    <Routes>
                    {routesWithElements.map((itm: any, index) => {
                      const Element = itm.Element;
                      return (
                        <Route
                          path={itm.url}
                          key={index}
                          element={
                            itm.path ? (
                              <Suspense fallback={<RouteFallback />}>
                                <PageRouter
                                  url={itm.url}
                                  auth={itm.auth ? true : false}
                                >
                                  <RouteKeyed>
                                    <Element />
                                  </RouteKeyed>
                                </PageRouter>
                              </Suspense>
                            ) : (
                              itm.element
                            )
                          }
                        />
                      );
                    })}
                    </Routes>
                  </RouteLayoutShell>
                  <ExplainerManager />
                  <DormantInviteClaimer />
                  <ScrollRestoration />
                </Router>
              </Suspense>
            </PersistGate>
          )}
        </WhiteLabelProvider>
        </CoachWebSocketProvider>
      </Provider>
      <div id="loader" className="loaderDiv d-none">
        <div>
          <img src="/assets/img/loader.gif" alt="logo" className="loaderlogo" />
        </div>
      </div>
      <ToastContainer position="top-right" className="toasterDiv" />
      <CookieConsent />
    </>
  );
}

export default App;
